import React from 'react';
import { NodeProps, useReactFlow } from 'reactflow';
import { updateNodeValue } from '../../../../../services/method';
import { operationType, selectDataType } from '../../../../../services/types';
import { Main, Styled } from './style';
import { SettingsBlock, settingsDataType } from '../../../../settingsBlock';
import { IoCheckmarkCircleOutline, IoWarningOutline } from 'react-icons/io5';
import { ExpandedBlock } from '../../../expanded';
import { AuthenticateSelect } from '../../../../auth/select';
import { Select } from '../../../../form/select';
import axios from 'axios';
import { webURL } from '../../../../../services/API';
import { Textarea } from '../../../../form/textarea';
import { TextInput } from '../../../../form/textInput';

type FlowQueueProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};
export function Notion({ id, data, setChildMinimize, setChildSettings }: FlowQueueProps) {
   const [requests, setRequests] = React.useState<selectDataType[]>([
      { id: 1, name: 'Append a block', refId: 'append_block', isActive: true }, 
      { id: 2, name: 'Get many child blocks', refId: 'get_child_blocks' }, 
      { id: 3, name: 'Get a database', refId: 'get_database' }, 
      // { id: 4, name: 'Create a database page', refId: 'create_database_page' },
      { id: 5, name: 'Get a database pages', refId: 'get_database_pages'}, 
      // { id: 6, name: 'Update a database page', refId: 'update_database_page' }, 
      { id: 7, name: 'Archive a page', refId: 'archive_page' }, 
      { id: 8, name: 'Create a page', refId: 'create_page' },
      { id: 9, name: 'Get a page', refId: 'get_page' },
      { id: 10, name: 'Get many users', refId: 'get_many_users' },
      { id: 11, name: 'Let AI Agent Decide', refId: 'agent_decide' }
   ]);
   const [blockTypes, setBlockTypes] = React.useState<selectDataType[]>([
      { id: 1, name: 'Paragraph', refId: 'paragraph', isActive: true }, 
      { id: 2, name: 'Heading 1', refId: 'heading_1' }, 
      { id: 3, name: 'Heading 2', refId: 'heading_3' }, 
      { id: 4, name: 'Heading 3', refId: 'heading_4' }, 
      { id: 5, name: 'Toggle', refId: 'toggle' },
      { id: 6, name: 'To-Do', refId: 'to_do' },
      { id: 7, name: 'Bulleted List Item', refId: 'bulleted_list_item'}, 
      { id: 8, name: 'Numbered List Item', refId: 'numbered_list_item' },
      { id: 9, name: 'Image', refId: 'image' },
   ]);
   const { setNodes } = useReactFlow();
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);
   
   // --
   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   // ---
   React.useEffect(() => {
      if (!data.value) updateNodeValue(id, setNodes, { request: requests[0].refId });
      else setRequests(requests => requests.map(request => ({ ...request, isActive: request.refId === data.value.request })));
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // ---
   React.useEffect(() => {
      if (data.value?.connection && !data.value?.pages) getPages();
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data.value?.connection, data.value?.pages]);

   // ---
   const getPages = async () => {
      const pages = (await axios.post(
         `${webURL}/general-extra/notionToken?type=pages`, { token: data.value?.connection.access_token }
      )).data;
      const pageId = data.value?.pages?.find((p: any) => p.isActive)?.id;
      updateNodeValue(id, setNodes, { 
         pages: pages.map((page: any, index: number) => ({ ...page, isActive: pageId ? pageId === page.id : index === 0 })) 
      });
      // get databases
      const databases = (await axios.post(
         `${webURL}/general-extra/notionToken?type=databases`, { token: data.value?.connection.access_token, limit: 25 }
      )).data;
      const databaseId = data.value?.databases?.find((p: any) => p.isActive).id;
      updateNodeValue(id, setNodes, { 
         databases: databases.map((database: any, index: number) => ({ ...database, isActive: databaseId ? databaseId === database.id : index === 0 })) 
      });
   }

   // ---
   const handleAuthenticate = async (type: 'add' | 'remove', data: any) => {
      if (type === 'add') { 
         updateNodeValue(id, setNodes, { connection: data });
      } else if (type === 'remove') {
         updateNodeValue(id, setNodes, { connection: null });
      }
   }

   // ---
   const handleSelect = (type: 'requests' | 'pages' | 'databases' | 'blockTypes', selectId: number) => {
      if (type === 'requests') {
         setRequests((requests) => requests.map((request) => {
            if (request.id === selectId) return { ...request, isActive: true };
            else return { ...request, isActive: false };
         }));
         updateNodeValue(id, setNodes, { request: requests.find(obj => obj.id === selectId)?.refId });

      } else if (type === 'pages' || type === 'databases') {
         updateNodeValue(id, setNodes, { [type]: data.value[type].map((i: any) => ({ ...i, isActive: i.id === selectId })) });

      } else if (type === 'blockTypes') {
         setBlockTypes((types) => types.map((type) => {
            if (type.id === selectId) return { ...type, isActive: true };
            else return { ...type, isActive: false };
         }));
         updateNodeValue(id, setNodes, { blockType: blockTypes.find(obj => obj.id === selectId)?.refId });
      }
   }
   // ---
   const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>, keyRef?: string) => {
      const value = e.target.value;
      if (keyRef) updateNodeValue(id, setNodes, { [keyRef]: value });
   }

   // --- 
   const isWarning = false;

   return (<>
      <SettingsBlock nodeId={ id } data={ settings } setData={ setSettings } />
      <Main>
         {isWarning ? <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
         </> : data.output ? <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
         </> : null}
         { isExpanded ? <ExpandedBlock
            data={{ id, ...data }} 
            handleClose={ () => setIsExpanded(false) } 
            isWarning={ isWarning }
            style={{ minWidth: '420px' }}
         ><Styled>
            <div className="action">
               <AuthenticateSelect type="notion" defaultId={ data.value?.connection?.id } handleAuthenticate={ handleAuthenticate } background={ data.background } />
               { data.value?.connection ? <button className="btn1 withBorder" onClick={ getPages }>Re-fetching pages</button> : null }
            </div>
            <Select title="Request" data={ requests } handleSelectItem={ (id) => handleSelect('requests', id) } isSmall={ true } />
            { ['append_block', 'get_child_blocks', 'archive_page', 'create_page', 'get_page'].includes(data.value?.request) ?
               <Select title="Page Id" data={ data.value?.pages || [] } handleSelectItem={ (id) => handleSelect('pages', id) } isSmall={ true } />
            : null }
            { data.value?.request === 'append_block' ? <>
               <Select title="Type" data={ blockTypes } handleSelectItem={ (id) => handleSelect('blockTypes', id) } isSmall={ true } />
               <Textarea
                  value={ data.value?.text } 
                  placeholder="Write the content" 
                  handleChange={ (e) => updateNodeValue(id, setNodes, { text: e.target.value }) } 
               /> 
            </> : null }
            { data.value?.request === 'get_database' ?
               <Select title="DB Id" data={ data.value?.databases || [] } handleSelectItem={ (id) => handleSelect('databases', id) } isSmall={ true } />
            : null }
            { data.value?.request === 'create_page' ?
               <TextInput keyRef="title" value={ data.value?.title } title="Title" placeholder="name_it" handleChange={ handleChangeInput } />
            : null }
            { ['get_child_blocks', 'get_database_pages', 'get_many_users'].includes(data.value?.request) ?
               <TextInput keyRef="limit" value={ data.value?.limit } title="Limit" placeholder="5" handleChange={ handleChangeInput } />
            : null }
         </Styled></ExpandedBlock> : null}
      </Main>
   </>);
}